// react components
import { useState, useEffect } from "react";

// mui components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

//components
import Item from "./layout/Item";
import GraphLayout from "./layout/GraphLayout";
import HorizontalBarGraphs from "../charts/HorizontalBarGraphs";
import DoughnutCharts from "../charts/DoughnutCharts";
import StatsTable from "./layout/StatsTable";
import ColumnVisibility from "../../common/ColumnVisibility";
import ComparisonLegend from "../../common/TTest/ComparisonLegend";

//redux
import { useSelector, useDispatch } from "react-redux";
import { loadAnalysisSwitchState } from "store/features/analysisResultsSlice";
import PropTypes from "prop-types";

import { hslToCss } from "../../../utils/analysisTable/analysisUtils";

function PageLayout({ mappings = {}, handleFilterSubmit = () => { }, tTest = {}, moduleName = "" }) {
    const dispatch = useDispatch();
    const { tTestLegends, isFisher, tTestResult, testLabel } = tTest;
    const [comparisonRows, setComparisonRows] = useState([]);
    const switchState = useSelector((state) => state.analysisResultsStore.analysisSwitchState);
    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.productPreferenceAnalysisResult);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const isPercentage = useSelector((state) => state.analysisResultsStore.percentageToggle);
    const productColors = useSelector((state) => state.analysisResultsStore.productColors);

    const [productsData, setProductsData] = useState([]);
    const [productsMapColors, setProductsMapColors] = useState([]);

    const fetchSwitchState = (key, value) => {
        dispatch(loadAnalysisSwitchState({ ...switchState, [key]: value }));
    };

    useEffect(() => {
        let datum = {};
        let productArray = [];
        let tempProducts = [];
        productsSecondaryDataState.forEach((ele) => {
            const switchKey = `${ele.backendVariable}__${ele.scores[0]?.coreDataId}`;
            datum[switchKey] = "statsTable";
            ele?.scores?.forEach((datum) => {
                const productTitle = datum.title + "__" + datum.coreDataId;
                if (!productArray.includes(productTitle)) {
                    productArray.push(productTitle);
                    tempProducts.push(datum);
                }
            });
        });
        setProductsData([...tempProducts]);
        if (Object.keys(switchState).length === 0) {
            dispatch(loadAnalysisSwitchState({ ...datum }));
        }
    }, [productsSecondaryDataState]);

    useEffect(() => {
        let obj = {};
        productsData.forEach((ele, i) => {
            const color1 = hslToCss(...productColors[i]);
            obj[ele?.title.split(" ").join("_")] = color1;
        });
        setProductsMapColors(obj);
    }, [productsData]);

    useEffect(() => {
        if (!productsSecondaryDataState.length) return;
        const filteredRow = productsSecondaryDataState.map((group) => {
            const compPart = group.scores.map((c) => `${c.title} / ${c.study_reference_title}`).join(" vs ");

            return {
                backendVariable: group.backendVariable,
                title: compPart,
                coreDataId: group.scores[0]?.coreDataId,
            };
        });
        const sortedData = filteredRow.sort((a, b) => {
            const getCompNumber = (str) => parseInt(str.split('_').pop());
            const coreA = Number(a?.coreDataId) || 0;
            const coreB = Number(b?.coreDataId) || 0;
            if (coreA !== coreB) {
                return coreA - coreB;
            }
            return getCompNumber(a?.backendVariable) - getCompNumber(b?.backendVariable);
        });
        setComparisonRows(sortedData);
    }, [productsSecondaryDataState]);

    const showGraphByType = (type, statsData, specificTTestResultForGraph) => {
        if (type) {
            switch (type.toLowerCase()) {
                case "horizontal_bar":
                    return (
                        <HorizontalBarGraphs
                            data={statsData}
                            productColors={productsMapColors}
                            tTest={{
                                tTestLegends: tTestLegends,
                                isFisher: isFisher,
                                tTestResult: specificTTestResultForGraph,
                                testLabel: testLabel,
                            }}
                        />
                    );

                case "full_donut":
                    return (
                        <DoughnutCharts
                            data={statsData}
                            productColors={productsMapColors}
                            tTest={{
                                tTestLegends: tTestLegends,
                                isFisher: isFisher,
                                tTestResult: specificTTestResultForGraph,
                                testLabel: testLabel,
                            }}
                        />
                    );

                case "default":
                    return <DoughnutCharts data={statsData} />;
                default:
                    return null;
            }
        }
    };

    const renderCards = () => {  
        const sortedData = [...(productsSecondaryDataState || [])].sort((a, b) => {
            const getCompNumber = (str) => parseInt(str.split('_').pop() || 0);
            const coreIdA = a.scores[0]?.coreDataId || 0;
            const coreIdB = b.scores[0]?.coreDataId || 0;
            if (coreIdA !== coreIdB) {
                return coreIdA - coreIdB;
            }
            return getCompNumber(a?.backendVariable) - getCompNumber(b?.backendVariable);
        });
        const cards = sortedData?.map((datum, index) => {
            const isBackendVariableExists = mappings?.displayLabels?.filter((ele) => datum.backendVariable.includes(ele.backendVariable));
            const visibilityKey = `checked--${datum?.backendVariable}__${datum?.scores?.[0]?.coreDataId}`;
            const chartType = datum?.choice === "single" ? "full_donut" : "horizontal_bar";
            const cardCoreDataId = datum.scores[0]?.coreDataId;
            const cardProductTitles = new Set(datum.scores.map(score => score.title.toLowerCase()));
            const matchingTTestResult = tTestResult?.find(result => {
                const isCoreDataIdMatch = result.coreDataId == cardCoreDataId;
                if (!isCoreDataIdMatch) {
                    return false;
                }
                if (!result.colors || result.colors.length !== cardProductTitles.size) {
                    return false;
                }
                const resultProductCodes = new Set(result.colors.map(c => c.productCode.toLowerCase()));
                for (const cardTitle of cardProductTitles) {
                    if (!resultProductCodes.has(cardTitle)) {
                        return false;
                    }
                }
                return true;
            });

            const isTTestApplied = !!matchingTTestResult;

            const specificTTestResult = matchingTTestResult ? [matchingTTestResult] : [];

            const sortedScores = [...datum.scores].sort((a, b) => {
                // Unilever products first  then competitor products.
                const competitorSort = a.isCompetitorProduct - b.isCompetitorProduct;
                if (competitorSort !== 0) {
                    return competitorSort;
                }
            
                // Group by coreDataId, numerically.
                const coreDataIdSort = a.coreDataId - b.coreDataId;
                if (coreDataIdSort !== 0) {
                    return coreDataIdSort;
                }

                return a.title.localeCompare(b.title);
            });

            const switchKey = `${datum.backendVariable}__${datum.scores[0]?.coreDataId}`;
            if (isBackendVariableExists?.length && !productColumnVisibility[visibilityKey]) {
                const key = `comparison-${index}`;
                const comparisonProductsArr = sortedScores?.map((ele) => `${ele?.title} / ${ele?.study_reference_title}`);
                const comparisonTitle = comparisonProductsArr.join(" vs ");
                return (
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={6}
                        key={key}
                    >
                        <Item>
                            <GraphLayout
                                title={comparisonTitle}
                                backendVariable={switchKey}
                                fetchSwitchState={fetchSwitchState}
                                sampleSize={datum?.scores?.[0]?.sampleSize}
                            >
                                {switchState[switchKey] === "statsTable" && (
                                    <StatsTable
                                        data={sortedScores}
                                        title={""}
                                        isPercentage={isPercentage}
                                        handleFilterSubmit={handleFilterSubmit}
                                        products={productsData}
                                        tTest={{
                                            tTestLegends: tTestLegends,
                                            isFisher: isFisher,
                                            tTestResult: specificTTestResult,
                                            testLabel: testLabel,
                                        }}
                                        productsSecondaryData={productsSecondaryDataState}
                                    />
                                )}
                                {switchState[switchKey] === "countGraph" && showGraphByType(chartType, sortedScores, specificTTestResult)}
                                {isTTestApplied && datum.scores.length === 2 && (
                                    <ComparisonLegend
                                        legends={sortedScores}
                                        moduleName={moduleName}
                                        withBottomMargin={false}
                                    />
                                )}
                            </GraphLayout>
                        </Item>
                    </Grid>
                );
            }
            return null;
        });

        return cards;
    };

    return (
        <Box
            sx={{
                flexGrow: 1,
                p: 1,
                mb: 1,
            }}
        >
            <ColumnVisibility
                productsSecondaryData={comparisonRows}
                pageName="productPreferenceAnalysis"
            />
            <Grid
                container
                spacing={{ xs: 2, md: 3 }}
                columns={{ xs: 4, sm: 8, md: 12, lg: 12 }}
                sx={{
                    m: 1,
                }}
            >
                {renderCards()}
            </Grid>

            {testLabel && tTestResult && tTestResult.length > 0 && (
                <Box sx={{ mt: 3, ml: 2, textAlign: "left" }}>
                    <Typography
                        sx={{
                            marginBottom: "6px",
                            color: "#495057",
                            fontWeight: 600,
                            fontSize: 12,
                        }}
                    >
                        {testLabel}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

PageLayout.propTypes = {
    mappings: PropTypes.object,
    handleFilterSubmit: PropTypes.func,
    tTest: PropTypes.object,
    moduleName: PropTypes.string,
};

export default PageLayout;
